import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button, Card, Modal, Radio, Form, Input, Select, Switch, Space, Empty,
  Spin, Tag, Popconfirm, message, Alert, Descriptions, Tooltip,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, DeleteOutlined, SettingOutlined, CheckCircleFilled,
  PlayCircleOutlined, SyncOutlined,
} from '@ant-design/icons';
import { gatewayApi } from '../services/api-services';

interface PlatformMeta {
  name: string;
  icon: string;
  connection: string;
  qr_support: boolean;
}

interface GatewayBinding {
  id: number;
  platform: string;
  platform_meta?: PlatformMeta;
  app_id: string;
  app_secret_masked: string;
  has_secret: boolean;
  domain: string;
  connection_mode: string;
  bot_name?: string;
  bot_open_id?: string;
  config: Record<string, any>;
  status: string;
  cc_connect_running?: boolean;
}

// Credential field display labels
const CRED_LABELS: Record<string, string> = {
  app_id: 'App ID',
  app_secret: 'App Secret',
  token: 'Bot Token',
  bot_token: 'Bot Token (xoxb-...)',
  app_token: 'App Token (xapp-...)',
  corp_id: 'Corp ID',
  corp_secret: 'Corp Secret',
  agent_id: 'Agent ID',
  ws_url: 'WebSocket URL',
  channel_secret: 'Channel Secret',
  channel_token: 'Channel Token',
  homeserver: 'Homeserver URL',
  access_token: 'Access Token',
};

const CRED_PLACEHOLDERS: Record<string, string> = {
  app_id: 'cli_xxxx 或 dingxxxx',
  app_secret: '应用密钥',
  token: '123456:ABC-DEF...',
  bot_token: 'xoxb-your-bot-token',
  app_token: 'xapp-your-app-level-token',
  corp_id: '企业ID',
  corp_secret: '应用Secret',
  agent_id: '1000002',
  ws_url: 'ws://127.0.0.1:3001',
  channel_secret: 'Channel Secret',
  channel_token: 'Channel Access Token',
  homeserver: 'https://matrix.org',
  access_token: 'syt_xxx_xxx',
};

const GROUP_POLICIES = [
  { value: 'open', label: '开放（任何人 @ 都响应）' },
  { value: 'allowlist', label: '白名单（仅允许的用户）' },
  { value: 'disabled', label: '禁用（不响应群消息）' },
];

const GatewayTab: React.FC<{ employeeId: number }> = ({ employeeId }) => {
  const [loading, setLoading] = useState(false);
  const [bindings, setBindings] = useState<GatewayBinding[]>([]);
  const [platformsMeta, setPlatformsMeta] = useState<Record<string, PlatformMeta>>({});
  const [platformCredentials, setPlatformCredentials] = useState<Record<string, string[]>>({});

  // add flow
  const [addOpen, setAddOpen] = useState(false);
  const [platform, setPlatform] = useState<string>('feishu');
  const [bindMethod, setBindMethod] = useState<'qr' | 'manual'>('manual');
  const [domain, setDomain] = useState<'feishu' | 'lark'>('feishu');

  // qr state
  const [qrImage, setQrImage] = useState('');
  const [qrStatus, setQrStatus] = useState<'idle' | 'waiting' | 'scaned' | 'success' | 'denied' | 'expired' | 'error'>('idle');
  const [qrLoading, setQrLoading] = useState(false);
  const pollRef = useRef<{ stop: boolean }>({ stop: false });

  // manual form (universal)
  const [manualForm] = Form.useForm();
  const [manualSubmitting, setManualSubmitting] = useState(false);

  // config edit
  const [configOpen, setConfigOpen] = useState(false);
  const [editing, setEditing] = useState<GatewayBinding | null>(null);
  const [configForm] = Form.useForm();
  const [starting, setStarting] = useState<number | null>(null);

  // Load platform metadata on mount
  useEffect(() => {
    gatewayApi.platformsMeta().then((res: any) => {
      setPlatformsMeta(res.data?.platforms || res.platforms || {});
      setPlatformCredentials(res.data?.credentials || res.credentials || {});
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await gatewayApi.list(employeeId);
      setBindings(res.items || res.data?.items || []);
    } catch {
      message.error('加载网关绑定失败');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { pollRef.current.stop = true; }, []);

  const boundPlatforms = new Set(bindings.map(b => b.platform));
  const allPlatformKeys = Object.keys(platformsMeta);

  const resetAddState = () => {
    pollRef.current.stop = true;
    setQrImage(''); setQrStatus('idle'); setQrLoading(false);
    setBindMethod('manual'); setDomain('feishu');
    const firstAvailable = allPlatformKeys.find(k => !boundPlatforms.has(k)) || 'feishu';
    setPlatform(firstAvailable);
    manualForm.resetFields();
  };
  const openAdd = () => { resetAddState(); setAddOpen(true); };
  const closeAdd = () => { pollRef.current.stop = true; setAddOpen(false); };

  const currentMeta = platformsMeta[platform];
  const currentCredFields = platformCredentials[platform] || [];
  const hasQrSupport = currentMeta?.qr_support && ['feishu', 'lark', 'weixin', 'dingtalk'].includes(platform);

  // ---- QR bind success callback ----
  const onBindSuccess = async (_gateway: any, token: { stop: boolean }) => {
    setQrStatus('success');
    message.success('绑定成功');
    await load();
    setTimeout(() => { if (!token.stop) closeAdd(); }, 1200);
  };

  // ---- QR flow (feishu/weixin/dingtalk) ----
  const startQr = async () => {
    pollRef.current.stop = true;
    const token = { stop: false };
    pollRef.current = token;
    setQrLoading(true); setQrStatus('idle'); setQrImage('');
    try {
      if (platform === 'feishu' || platform === 'lark') {
        const res: any = await gatewayApi.feishuQrBegin(employeeId, domain);
        setQrImage(res.qr_image || ''); setQrStatus('waiting');
        const intervalMs = Math.max(2, res.interval || 5) * 1000;
        const deadline = Date.now() + (res.expire_in || 600) * 1000;
        const deviceCode = res.device_code;
        const poll = async () => {
          if (token.stop) return;
          if (Date.now() > deadline) { setQrStatus('expired'); return; }
          try {
            const r: any = await gatewayApi.feishuQrPoll(employeeId, deviceCode, res.domain || domain);
            if (token.stop) return;
            if (r.status === 'success') return onBindSuccess(r.gateway, token);
            if (r.status === 'denied') { setQrStatus('denied'); return; }
            if (r.status === 'expired') { setQrStatus('expired'); return; }
          } catch { /* transient */ }
          setTimeout(poll, intervalMs);
        };
        setTimeout(poll, intervalMs);
      } else if (platform === 'weixin') {
        const res: any = await gatewayApi.weixinQrBegin(employeeId);
        setQrImage(res.qr_image || ''); setQrStatus('waiting');
        const intervalMs = Math.max(2, res.interval || 2) * 1000;
        const deadline = Date.now() + (res.expire_in || 240) * 1000;
        const qrcode = res.qrcode;
        let baseUrl = res.base_url;
        const poll = async () => {
          if (token.stop) return;
          if (Date.now() > deadline) { setQrStatus('expired'); return; }
          try {
            const r: any = await gatewayApi.weixinQrPoll(employeeId, qrcode, baseUrl);
            if (token.stop) return;
            if (r.status === 'success') return onBindSuccess(r.gateway, token);
            if (r.base_url) baseUrl = r.base_url;
            if (r.status === 'scaned') setQrStatus('scaned');
            if (r.status === 'expired') { setQrStatus('expired'); return; }
            if (r.status === 'error') { setQrStatus('error'); message.error(r.error || '扫码失败'); return; }
          } catch { /* transient */ }
          setTimeout(poll, intervalMs);
        };
        setTimeout(poll, intervalMs);
      } else if (platform === 'dingtalk') {
        const res: any = await gatewayApi.dingtalkQrBegin(employeeId);
        setQrImage(res.qr_image || ''); setQrStatus('waiting');
        const intervalMs = Math.max(2, res.interval || 3) * 1000;
        const deadline = Date.now() + (res.expire_in || 7200) * 1000;
        const deviceCode = res.device_code;
        const poll = async () => {
          if (token.stop) return;
          if (Date.now() > deadline) { setQrStatus('expired'); return; }
          try {
            const r: any = await gatewayApi.dingtalkQrPoll(employeeId, deviceCode);
            if (token.stop) return;
            if (r.status === 'success') return onBindSuccess(r.gateway, token);
            if (r.status === 'denied') { setQrStatus('denied'); return; }
            if (r.status === 'expired') { setQrStatus('expired'); return; }
            if (r.status === 'error') { setQrStatus('error'); return; }
          } catch { /* transient */ }
          setTimeout(poll, intervalMs);
        };
        setTimeout(poll, intervalMs);
      }
    } catch (e: any) {
      setQrStatus('error');
      message.error(e?.response?.data?.detail || '发起扫码失败');
    } finally { setQrLoading(false); }
  };

  // auto-start QR
  useEffect(() => {
    if (addOpen && bindMethod === 'qr' && qrStatus === 'idle' && !boundPlatforms.has(platform) && hasQrSupport) {
      startQr();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOpen, platform, bindMethod, domain]);

  // weixin has no manual method
  useEffect(() => {
    if (platform === 'weixin') setBindMethod('qr');
    if (!hasQrSupport) setBindMethod('manual');
  }, [platform, hasQrSupport]);

  // ---- Manual submission (universal) ----
  const submitManual = async () => {
    const values = await manualForm.validateFields();
    setManualSubmitting(true);
    try {
      // For feishu/dingtalk use their dedicated endpoints for backward compat
      if (platform === 'feishu' || platform === 'lark') {
        await gatewayApi.feishuManual(employeeId, {
          app_id: values.app_id?.trim(),
          app_secret: values.app_secret?.trim(),
          domain: values.domain || domain || 'feishu',
        });
      } else if (platform === 'dingtalk') {
        await gatewayApi.dingtalkManual(employeeId, {
          app_id: values.app_id?.trim(),
          app_secret: values.app_secret?.trim(),
        });
      } else {
        // Universal bind endpoint for all other platforms
        const credentials: Record<string, string> = {};
        for (const field of currentCredFields) {
          credentials[field] = (values[field] || '').trim();
        }
        await gatewayApi.bind(employeeId, { platform, credentials, config: {} });
      }
      message.success('绑定成功');
      await load();
      closeAdd();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '绑定失败');
    } finally { setManualSubmitting(false); }
  };

  // ---- Config edit ----
  const openConfig = (b: GatewayBinding) => {
    setEditing(b);
    configForm.setFieldsValue({
      allow_all_users: b.config?.allow_all_users !== false,
      allowed_users: b.config?.allowed_users || [],
      group_policy: b.config?.group_policy || 'open',
      require_mention: b.config?.require_mention !== false,
      home_channel: b.config?.home_channel || '',
    });
    setConfigOpen(true);
  };

  const submitConfig = async () => {
    if (!editing) return;
    const values = await configForm.validateFields();
    try {
      await gatewayApi.updateConfig(employeeId, editing.id, values);
      message.success('配置已保存');
      setConfigOpen(false);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '保存失败');
    }
  };

  const syncGateway = async (b: GatewayBinding) => {
    try {
      await gatewayApi.sync(employeeId, b.id);
      message.success('已同步 cc-connect 配置');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '同步失败');
    }
  };

  const startGateway = async (b: GatewayBinding) => {
    setStarting(b.id);
    try {
      await gatewayApi.start(employeeId, b.id);
      message.success('网关已启动');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '启动失败');
    } finally { setStarting(null); }
  };

  const reinstall = async (b: GatewayBinding) => {
    try {
      await gatewayApi.reinstall(employeeId, b.id);
      message.success('已重新安装并启动');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '重新安装失败');
    }
  };

  const remove = async (b: GatewayBinding) => {
    try {
      await gatewayApi.remove(employeeId, b.id);
      message.success('已解绑');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '解绑失败');
    }
  };

  const getMeta = (key: string): PlatformMeta => platformsMeta[key] || { name: key, icon: '🔌', connection: '', qr_support: false };

  const renderQrStatus = () => {
    const platformName = currentMeta?.name || platform;
    switch (qrStatus) {
      case 'waiting': return <span style={{ color: 'var(--apple-text-secondary)' }}>请使用 {platformName} 手机客户端扫码并确认…</span>;
      case 'scaned': return <span style={{ color: 'var(--apple-text-secondary)' }}>已扫码，请在手机上确认…</span>;
      case 'success': return <span style={{ color: '#52c41a' }}><CheckCircleFilled /> 绑定成功</span>;
      case 'denied': return <span style={{ color: '#ff4d4f' }}>已取消授权，可重新生成二维码</span>;
      case 'expired': return <span style={{ color: '#ff4d4f' }}>二维码已过期，请重新生成</span>;
      case 'error': return <span style={{ color: '#ff4d4f' }}>发起失败，请重试</span>;
      default: return null;
    }
  };

  const renderCardInfo = (b: GatewayBinding) => {
    const meta = getMeta(b.platform);
    return (
      <Descriptions column={1} size="small">
        {b.bot_name && <Descriptions.Item label="名称">{b.bot_name}</Descriptions.Item>}
        {b.app_id && (
          <Descriptions.Item label="ID">
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{b.app_id}</span>
          </Descriptions.Item>
        )}
        {b.has_secret && (
          <Descriptions.Item label="密钥">
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{b.app_secret_masked}</span>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="连接">
          <Tag>{b.connection_mode || meta.connection}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="访问">
          {b.config?.allow_all_users
            ? <Tag color="warning">所有人可用</Tag>
            : <Tag>白名单 {b.config?.allowed_users?.length || 0} 人</Tag>}
        </Descriptions.Item>
      </Descriptions>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: 'var(--apple-text-secondary)' }}>
          将数字人绑定到消息网关，绑定后可在对应平台直接与该数字人对话。支持 {allPlatformKeys.length} 个平台。
        </span>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>新增网关</Button>
      </div>

      <Spin spinning={loading}>
        {bindings.length === 0 ? (
          <Empty description="暂无网关绑定" style={{ padding: '40px 0' }} />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {bindings.map(b => {
              const meta = getMeta(b.platform);
              return (
                <Card
                  key={b.id}
                  style={{ width: 340, display: 'flex', flexDirection: 'column' }}
                  styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column' } }}
                  title={<Space>
                    <span style={{ fontSize: 18 }}>{meta.icon}</span>
                    {meta.name}
                    {b.status === 'bound'
                      ? <Tag color="success">已绑定</Tag>
                      : <Tag color="error">{b.status}</Tag>}
                  </Space>}
                  extra={
                    <Space size={4}>
                      {b.cc_connect_running
                        ? <Tag color="processing" style={{ marginInlineEnd: 0 }}>运行中</Tag>
                        : <Tag color="default" style={{ marginInlineEnd: 0 }}>未运行</Tag>}
                    </Space>
                  }
                  actions={[
                    <Space key="cfg" onClick={() => openConfig(b)}><SettingOutlined /> 配置</Space>,
                    <Tooltip key="sync" title="同步配置到 cc-connect 并重启">
                      <Space onClick={() => syncGateway(b)}><SyncOutlined /> 同步</Space>
                    </Tooltip>,
                    <Popconfirm key="del" title="确定解绑？" onConfirm={() => remove(b)}>
                      <Space style={{ color: '#ff4d4f' }}><DeleteOutlined /> 解绑</Space>
                    </Popconfirm>,
                  ]}
                >
                  {renderCardInfo(b)}
                  <div style={{ flex: 1 }} />
                  {!b.cc_connect_running && (
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      loading={starting === b.id}
                      onClick={() => startGateway(b)}
                      block
                      style={{ marginTop: 12 }}
                    >
                      启动网关
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </Spin>

      {/* Add modal */}
      <Modal
        title="新增网关绑定"
        open={addOpen}
        onCancel={closeAdd}
        footer={bindMethod === 'manual' ? [
          <Button key="cancel" onClick={closeAdd}>取消</Button>,
          <Button key="ok" type="primary" loading={manualSubmitting} onClick={submitManual}>绑定</Button>,
        ] : [
          <Button key="cancel" onClick={closeAdd}>关闭</Button>,
        ]}
        width={560}
        destroyOnHidden
      >
        {/* Platform selector */}
        <Form layout="vertical" style={{ marginBottom: 4 }}>
          <Form.Item label="网关平台" style={{ marginBottom: 16 }}
            help={boundPlatforms.has(platform) ? '该平台已绑定，如需更换请先解绑' : undefined}
            validateStatus={boundPlatforms.has(platform) ? 'warning' : undefined}
          >
            <Select
              value={platform}
              onChange={(v) => { setPlatform(v); setQrStatus('idle'); manualForm.resetFields(); }}
              options={allPlatformKeys.map(key => {
                const m = platformsMeta[key];
                const bound = boundPlatforms.has(key);
                return {
                  value: key,
                  disabled: bound,
                  label: (
                    <Space>
                      <span>{m?.icon}</span>
                      <span>{m?.name || key}</span>
                      <span style={{ fontSize: 11, color: '#999' }}>{m?.connection}</span>
                      {bound && <span style={{ fontSize: 12, color: 'var(--apple-text-secondary)' }}>（已绑定）</span>}
                    </Space>
                  ),
                };
              })}
            />
          </Form.Item>
        </Form>

        {/* Bind method selector (only for QR-capable platforms) */}
        {hasQrSupport && platform !== 'weixin' && (
          <Radio.Group
            value={bindMethod}
            onChange={(e) => { setBindMethod(e.target.value); if (e.target.value === 'qr') setQrStatus('idle'); }}
            style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}
          >
            <Radio value="qr">扫码绑定（推荐）</Radio>
            <Radio value="manual">手动输入凭证</Radio>
          </Radio.Group>
        )}

        {/* QR view */}
        {bindMethod === 'qr' && hasQrSupport && (
          <div style={{ textAlign: 'center' }}>
            {(platform === 'feishu' || platform === 'lark') && (
              <Space style={{ marginBottom: 12 }}>
                <span>地区：</span>
                <Select value={domain} onChange={(v) => { setDomain(v); setQrStatus('idle'); }} style={{ width: 160 }}
                  options={[{ value: 'feishu', label: '飞书（中国）' }, { value: 'lark', label: 'Lark（国际）' }]} />
              </Space>
            )}
            {platform === 'weixin' && (
              <div style={{ marginBottom: 12, color: 'var(--apple-text-secondary)', fontSize: 13 }}>
                使用微信扫码登录；一个微信号只能绑定一个数字人
              </div>
            )}
            <div style={{
              minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--apple-bg-secondary, #fafafa)', borderRadius: 8, padding: 16,
            }}>
              {qrLoading ? <Spin /> : qrImage ? (
                <img src={qrImage} alt="扫码" style={{ width: 200, height: 200, background: '#fff', padding: 8, borderRadius: 4 }} />
              ) : <span style={{ color: 'var(--apple-text-secondary)' }}>二维码加载中...</span>}
            </div>
            <div style={{ marginTop: 12, minHeight: 22 }}>{renderQrStatus()}</div>
            {(qrStatus === 'expired' || qrStatus === 'denied' || qrStatus === 'error') && (
              <Button onClick={startQr} icon={<ReloadOutlined />} style={{ marginTop: 8 }}>重新生成二维码</Button>
            )}
          </div>
        )}

        {/* Manual form (universal) */}
        {bindMethod === 'manual' && (
          <Form form={manualForm} layout="vertical">
            {(platform === 'feishu' || platform === 'lark') && (
              <Form.Item name="domain" label="地区" initialValue="feishu">
                <Select options={[{ value: 'feishu', label: '飞书（中国）' }, { value: 'lark', label: 'Lark（国际）' }]} />
              </Form.Item>
            )}
            {currentCredFields.map(field => (
              <Form.Item key={field} name={field} label={CRED_LABELS[field] || field}
                rules={[{ required: true, message: `请输入 ${CRED_LABELS[field] || field}` }]}>
                {field.includes('secret') || field.includes('token') || field === 'access_token'
                  ? <Input.Password placeholder={CRED_PLACEHOLDERS[field] || ''} />
                  : <Input placeholder={CRED_PLACEHOLDERS[field] || ''} />}
              </Form.Item>
            ))}

          </Form>
        )}
      </Modal>

      {/* Config modal */}
      <Modal
        title={`网关配置 - ${editing ? getMeta(editing.platform).name : ''}`}
        open={configOpen}
        onCancel={() => setConfigOpen(false)}
        onOk={submitConfig}
        okText="保存"
        width={520}
        destroyOnHidden
      >
        <Form form={configForm} layout="vertical">
          <Form.Item name="allow_all_users" label="允许所有用户" valuePropName="checked"
            tooltip="开启后任何人都能使用；生产环境建议关闭并配置白名单">
            <Switch />
          </Form.Item>
          <Form.Item name="allowed_users" label="允许的用户 ID" tooltip={'未开启"允许所有用户"时生效'}>
            <Select mode="tags" placeholder="用户ID，回车添加" tokenSeparators={[',']} />
          </Form.Item>
          <Form.Item name="group_policy" label="群消息策略">
            <Select options={GROUP_POLICIES} />
          </Form.Item>
          <Form.Item name="require_mention" label="群内需 @ 提及" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="home_channel" label="Home Channel" tooltip="通知/cron 输出的目标会话，可留空">
            <Input placeholder="可留空" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GatewayTab;
